import { dateTime } from 'aos-helpers/src/helpers/Time'
import { uuid } from 'aos-helpers/src/helpers/Uuid'
import {
    MaintenanceContractorPayload,
    MaintenanceJobAssignmentPayload,
    MaintenanceTaskFormPayload,
} from 'aos-services/src/services/tasks/types/payload/MaintenanceTaskFormPayload'
import {
    MaintenanceTaskType,
    translateMaintenanceJob,
} from 'aos-services/src/services/tasks/types/task/MaintenanceTask'
import { TaskMetadata } from 'aos-services/src/services/tasks/types/TaskMetadata'
import { Box, DateTimePicker, HStack, Input, InputField, Text, VStack } from 'aos-ui-universal'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { Controller, useController, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { MaintenanceJobAssignmentRenderer } from './partials/MaintenanceJobAssignmentRenderer'
import { TaskEditableListField } from './partials/TaskEditableListField'

interface MaintenanceTaskFormProps {
    metadata: TaskMetadata
    isTaskTemplate: boolean
}

export const MaintenanceTaskForm: FC<MaintenanceTaskFormProps> = ({ metadata, isTaskTemplate }) => {
    const { t: translate } = useTranslation()
    const { control } = useFormContext<MaintenanceTaskFormPayload>()
    const payload = useWatch({ control }) as MaintenanceTaskFormPayload
    const [prevPayload, setPrevPayload] = useState(payload)
    const titleField = useController({ control, name: 'title' })

    useEffect(() => {
        setPrevPayload(payload)
    }, [payload])

    const changeTitle = useCallback(
        (
            assignments: MaintenanceJobAssignmentPayload[],
            onChangeAssignments: (next: MaintenanceJobAssignmentPayload[]) => void,
        ) => {
            if (
                prevPayload?.assignments[0]?.brushesVehicle?.label !==
                    assignments[0]?.brushesVehicle?.label &&
                assignments[0]?.job !== undefined &&
                assignments[0]?.job?.jobType === MaintenanceTaskType.Brushes
            ) {
                onChangeAssignments(assignments)
                titleField.field.onChange(assignments[0]?.brushesVehicle?.label)
                return
            } else if (
                prevPayload?.assignments[0]?.job === undefined ||
                prevPayload?.assignments[0]?.job?.label !== assignments[0]?.job?.label
            ) {
                onChangeAssignments(assignments)
                const job = assignments[0]?.job
                titleField.field.onChange(job ? translateMaintenanceJob(translate)(job) : undefined)
                return
            } else if (assignments[0]?.brushesVehicle === undefined) {
                onChangeAssignments(assignments)
                titleField.field.onChange(undefined)
                return
            }

            onChangeAssignments(assignments)
        },
        [prevPayload, titleField.field, translate],
    )

    return (
        <>
            <Controller
                control={control}
                name='assignments'
                render={({ field, fieldState }) => (
                    <TaskEditableListField
                        keyPrefix='tasks.form.maintenance.assignments'
                        value={field.value}
                        className='mb-3'
                        isError={!!fieldState.error}
                        required={!isTaskTemplate}
                        separated
                        emptyItem={{ uuid: uuid() }}
                        onChange={assignments => changeTitle(assignments, field.onChange)}
                        itemRenderer={({ item, onChangeItem }) => (
                            <MaintenanceJobAssignmentRenderer
                                maintenanceJobs={metadata.maintenanceJobs}
                                assignment={item}
                                metadata={metadata}
                                onChange={onChangeItem}
                                isTaskTemplate={isTaskTemplate}
                            />
                        )}
                    />
                )}
            />
        </>
    )
}

interface MaintenanceContractorRendererProps {
    contractor: MaintenanceContractorPayload
    onChange(v: MaintenanceContractorPayload): void
}

export const MaintenanceContractorRenderer: FC<MaintenanceContractorRendererProps> = ({
    contractor,
    onChange,
}) => {
    const { t: translate } = useTranslation()
    const datetimeRow = (
        label: string,
        placeholder: string,
        value: MaintenanceContractorPayload['contractTime'],
        onChangeValue: (v: MaintenanceContractorPayload['contractTime']) => void,
    ) => (
        <HStack className='items-center gap-5'>
            <Text size='body' tone='default' className='min-w-0 flex-1'>
                {label}
            </Text>
            <Box className='flex-1'>
                <DateTimePicker
                    placeholder={placeholder}
                    value={value ? dateTime(value).toDate() : null}
                    onChange={date => onChangeValue(dateTime(date))}
                />
            </Box>
        </HStack>
    )

    return (
        <VStack className='mr-4 flex-1 gap-3'>
            <HStack className='gap-5'>
                <Input className='flex-1'>
                    <InputField
                        placeholder={translate('form.contractor.name.placeholder', {
                            ns: 'tasks',
                        })}
                        value={contractor.name ?? ''}
                        onChangeText={name => onChange({ ...contractor, name })}
                    />
                </Input>
                <Input className='flex-1'>
                    <InputField
                        placeholder={translate('form.contractor.number-of-workers.placeholder', {
                            ns: 'tasks',
                        })}
                        keyboardType='numeric'
                        value={
                            contractor.numberOfWorkers !== undefined
                                ? String(contractor.numberOfWorkers)
                                : ''
                        }
                        onChangeText={v => {
                            const digits = v.replace(/\D/g, '')
                            onChange({
                                ...contractor,
                                numberOfWorkers: digits ? Number(digits) : undefined,
                            })
                        }}
                    />
                </Input>
            </HStack>
            {datetimeRow(
                translate('form.contractor.contact-time.label', { ns: 'tasks' }),
                translate('form.contractor.contact-time.placeholder', { ns: 'tasks' }),
                contractor.contractTime,
                contractTime => onChange({ ...contractor, contractTime }),
            )}
            {datetimeRow(
                translate('form.contractor.job-start.label', { ns: 'tasks' }),
                translate('form.contractor.job-start.placeholder', { ns: 'tasks' }),
                contractor.jobStart,
                jobStart => onChange({ ...contractor, jobStart }),
            )}
            {datetimeRow(
                translate('form.contractor.job-end.label', { ns: 'tasks' }),
                translate('form.contractor.job-end.placeholder', { ns: 'tasks' }),
                contractor.jobEnd,
                jobEnd => onChange({ ...contractor, jobEnd }),
            )}
        </VStack>
    )
}
